import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';

import type { MediaSource } from '@/entities/course/model/dto';
import { useAudioStore } from '@/shared/state/audio-store';

const isHlsSource = (mimeType: string) =>
  mimeType === 'application/x-mpegURL' ||
  mimeType === 'application/vnd.apple.mpegurl';

const isDashSource = (mimeType: string) => mimeType === 'application/dash+xml';

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

interface DescriptionPlayerState {
  isReady: boolean;
  isPlaying: boolean;
  isMuted: boolean;
  isEnded: boolean;
  currentTime: number;
  duration: number;
  progressPercent: number;
}

const createInitialState = (): DescriptionPlayerState => ({
  isReady: false,
  isPlaying: false,
  isMuted: false,
  isEnded: false,
  currentTime: 0,
  duration: 0,
  progressPercent: 0,
});

export const useDescriptionAudioPlayer = (source: MediaSource | null) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const dashRef = useRef<import('dashjs').MediaPlayerClass | null>(null);
  const shouldAutoplayRef = useRef(false);

  const globalMuted = useAudioStore((state) => state.isMuted);
  const setGlobalMuted = useAudioStore((state) => state.setMuted);

  const [state, setState] = useState<DescriptionPlayerState>(() => ({
    ...createInitialState(),
    isMuted: globalMuted,
  }));

  const mimeType = source?.mime_type ?? null;
  const mediaUrl = source?.url ?? null;

  const audioElement = audioRef.current;

  useEffect(() => {
    const audio = audioElement;
    if (!audio) return;

    const handlePlay = () =>
      setState((prev) => ({
        ...prev,
        isPlaying: true,
        isEnded: false,
      }));

    const handlePause = () =>
      setState((prev) => ({
        ...prev,
        isPlaying: false,
      }));

    const handleTimeUpdate = () => {
      const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
      const currentTime = audio.currentTime || 0;
      const progressPercent =
        duration > 0 ? clamp((currentTime / duration) * 100, 0, 100) : 0;

      setState((prev) => ({
        ...prev,
        currentTime,
        duration: duration || prev.duration,
        progressPercent,
      }));
    };

    const handleLoadedMetadata = () =>
      setState((prev) => ({
        ...prev,
        duration: Number.isFinite(audio.duration)
          ? audio.duration
          : prev.duration,
      }));

    const handleCanPlay = () => {
      setState((prev) => ({
        ...prev,
        isReady: true,
      }));

      if (shouldAutoplayRef.current) {
        shouldAutoplayRef.current = false;
        void audio.play().catch(() => undefined);
      }
    };

    const handleEnded = () =>
      setState((prev) => ({
        ...prev,
        isEnded: true,
        isPlaying: false,
        progressPercent: 100,
      }));

    const handleVolumeChange = () =>
      setState((prev) => ({
        ...prev,
        isMuted: audio.muted,
      }));

    const handleWaiting = () =>
      setState((prev) => ({
        ...prev,
        isReady: false,
      }));

    const handlePlaying = () =>
      setState((prev) => ({
        ...prev,
        isReady: true,
      }));

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('volumechange', handleVolumeChange);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handlePlaying);

    return () => {
      audio.pause();
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('volumechange', handleVolumeChange);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handlePlaying);
    };
  }, [audioElement]);

  const resetPlayers = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (dashRef.current) {
      dashRef.current.reset();
      dashRef.current = null;
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    shouldAutoplayRef.current = true;

    const currentMuted = useAudioStore.getState().isMuted;
    setState((prev) => ({
      ...createInitialState(),
      isMuted: currentMuted,
    }));
    audio.muted = currentMuted;

    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    resetPlayers();

    if (!mediaUrl || !mimeType) {
      return;
    }

    const initialisePlayback = async () => {
      if (typeof window === 'undefined') return;

      if (isHlsSource(mimeType)) {
        if (Hls.isSupported()) {
          const hls = new Hls();
          hlsRef.current = hls;
          hls.loadSource(mediaUrl);
          hls.attachMedia(audio);
        } else {
          audio.src = mediaUrl;
          audio.load();
        }
      } else if (isDashSource(mimeType)) {
        const dashModule = (await import('dashjs')) as typeof import('dashjs');
        const dashPlayer = dashModule.MediaPlayer().create();
        dashPlayer.initialize(audio, mediaUrl, false);
        dashRef.current = dashPlayer;
      } else {
        audio.src = mediaUrl;
        audio.load();
      }
    };

    void initialisePlayback();

    return () => {
      resetPlayers();
    };
  }, [mediaUrl, mimeType, resetPlayers]);

  // Sync audio element muted state with global store (without triggering reload)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = globalMuted;
    setState((prev) => ({
      ...prev,
      isMuted: globalMuted,
    }));
  }, [globalMuted]);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    shouldAutoplayRef.current = false;
    setState((prev) => ({
      ...prev,
      isPlaying: true,
      isEnded: false,
    }));

    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      void playPromise.catch(() => {
        setState((prev) => ({
          ...prev,
          isPlaying: false,
        }));
      });
    }
  }, []);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    shouldAutoplayRef.current = false;
    audio.pause();
    setState((prev) => ({
      ...prev,
      isPlaying: false,
    }));
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const newMutedState = !audio.muted;
    audio.muted = newMutedState;
    setGlobalMuted(newMutedState);
    setState((prev) => ({
      ...prev,
      isMuted: newMutedState,
    }));
  }, [setGlobalMuted]);

  const restart = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.currentTime = 0;
      shouldAutoplayRef.current = false;
      setState((prev) => ({
        ...prev,
        isPlaying: true,
        isEnded: false,
        currentTime: 0,
        progressPercent: 0,
      }));

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        void playPromise.catch(() => {
          setState((prev) => ({
            ...prev,
            isPlaying: false,
          }));
        });
      }
    } catch {
      // ignore errors
    }
  }, []);

  const seekTo = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
    const nextTime = clamp(time, 0, duration || time);

    try {
      audio.currentTime = nextTime;
      setState((prev) => ({
        ...prev,
        currentTime: nextTime,
        progressPercent:
          duration > 0 ? clamp((nextTime / duration) * 100, 0, 100) : 0,
      }));
    } catch {
      // ignore seek errors
    }
  }, []);

  const controls = useMemo(
    () => ({
      play,
      pause,
      toggleMute,
      restart,
      seekTo,
    }),
    [pause, play, restart, seekTo, toggleMute],
  );

  return {
    audioRef,
    state,
    controls,
    hasSource: Boolean(mediaUrl),
  };
};
